import React from "react";
import { StyleSheet, View } from "react-native";
import { Box, ScrollView, Text } from "native-base";
import FastImage from "@d11/react-native-fast-image";

import { useAppContext } from "../../../Universal/API/Context-API/AppContext";
import PressableWrapp from "../../../Universal/Buttons/PressableReact/PressableWrapp";
import { webFont } from "../../../Universal/API/API";
import { CatalogTypeItem } from "../../types";
import { getCatalogTypeDisplayName } from "../../utils";

type TypeCardProps = {
  item: CatalogTypeItem;
  isActive: boolean;
  width: number;
  onPress: () => void;
  scaleAll: number;
};

const createCardStyles = (scaleAll: number) =>
  StyleSheet.create({
    card: {
      borderRadius: 20 * scaleAll,
      overflow: "hidden",
      backgroundColor: "#FFFFFF",
      borderWidth: 1,
      borderColor: "#ECE8E1",
    },
    cardActive: {
      borderColor: "#111111",
      shadowColor: "#000000",
      shadowOpacity: 0.12,
      shadowRadius: 28,
      shadowOffset: { width: 0, height: 12 },
      elevation: 8,
    },
    cardHovered: {
      shadowColor: "#000000",
      shadowOpacity: 0.1,
      shadowRadius: 24,
      shadowOffset: { width: 0, height: 10 },
      elevation: 7,
    },
    mediaWrap: {
      position: "relative",
      width: "100%",
      aspectRatio: 1.14,
      overflow: "hidden",
      backgroundColor: "#FFFFFF",
    },
    mediaBackdrop: {
      position: "absolute",
      inset: 0,
      backgroundImage:
        "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.88), rgba(255,255,255,0) 42%), linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,255,255,0.94))",
    } as any,
    image: {
      width: "100%",
      height: "100%",
    },
    overlay: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      paddingHorizontal: 14 * scaleAll,
      paddingTop: 24 * scaleAll,
      paddingBottom: 14 * scaleAll,
      backgroundImage:
        "linear-gradient(to top, rgba(17,17,17,0.86), rgba(17,17,17,0.36), rgba(17,17,17,0.02))",
    } as any,
    title: {
      fontFamily: webFont("Inter"),
      fontSize: 16 * scaleAll,
      lineHeight: 18 * scaleAll,
      color: "#FFFFFF",
      fontWeight: "700",
      marginBottom: 6 * scaleAll,
    },
    chipRow: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 8 * scaleAll,
      alignItems: "center",
    },
    chip: {
      minHeight: 24 * scaleAll,
      borderRadius: 999,
      paddingHorizontal: 9 * scaleAll,
      paddingVertical: 4 * scaleAll,
      backgroundColor: "rgba(255,255,255,0.92)",
      borderWidth: 1,
      borderColor: "rgba(255,255,255,0.2)",
      alignItems: "center",
      justifyContent: "center",
    },
    chipActive: {
      backgroundColor: "#111111",
      borderColor: "#111111",
    },
    chipText: {
      fontFamily: webFont("Inter"),
      fontSize: 9 * scaleAll,
      color: "#3C3732",
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.45,
    },
    chipTextActive: {
      color: "#FFFFFF",
    },
  });

const TypeCard: React.FC<TypeCardProps> = React.memo(
  ({ item, isActive, width, onPress, scaleAll }) => {
    const styles = React.useMemo(() => createCardStyles(scaleAll), [scaleAll]);
    const [hovered, setHovered] = React.useState(false);

    return (
      <PressableWrapp
        onPress={onPress}
        activeOpacity={0.88}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        style={{ width }}
      >
        <View
          style={[
            styles.card,
            hovered ? styles.cardHovered : null,
            isActive ? styles.cardActive : null,
          ]}
        >
          <View style={styles.mediaWrap}>
            <View style={styles.mediaBackdrop} />

            {item.image ? (
              <FastImage
                style={styles.image}
                source={{ uri: item.image }}
                resizeMode={FastImage.resizeMode.contain}
              />
            ) : null}

            <View style={styles.overlay}>
              <Text selectable={false} style={styles.title}>
                {getCatalogTypeDisplayName(item.type)}
              </Text>

              <View style={styles.chipRow}>
                <View style={[styles.chip, isActive ? styles.chipActive : null]}>
                  <Text
                    selectable={false}
                    style={[styles.chipText, isActive ? styles.chipTextActive : null]}
                  >
                    {isActive ? "Выбрано" : "Открыть"}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </PressableWrapp>
    );
  }
);

type TypeSlideProps = {
  catalogTypes: CatalogTypeItem[];
  selectedTypeIndex: number | null;
  onSelectType: (index: number) => void;
  height: number;
  width: number;
};

const createSlideStyles = (scaleAll: number) =>
  StyleSheet.create({
    content: {
      paddingHorizontal: 16 * scaleAll,
      paddingTop: 42 * scaleAll,
      paddingBottom: 88 * scaleAll,
      alignItems: "center",
    },
    shell: {
      width: "100%",
      maxWidth: 1560 * scaleAll,
      alignSelf: "center",
    },
    hero: {
      paddingTop: 10 * scaleAll,
      paddingHorizontal: 4 * scaleAll,
      marginBottom: 10 * scaleAll,
    },
    heroTitle: {
      fontFamily: webFont("Inter"),
      fontSize: 22 * scaleAll,
      lineHeight: 27 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 4 * scaleAll,
      maxWidth: 900 * scaleAll,
    },
    heroSubtitle: {
      fontFamily: webFont("Inter"),
      fontSize: 14 * scaleAll,
      lineHeight: 19 * scaleAll,
      color: "#6F6A63",
      fontWeight: "500",
      maxWidth: 760 * scaleAll,
    },
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 10 * scaleAll,
      alignItems: "flex-start",
    },
  });

const TypeSlide: React.FC<TypeSlideProps> = React.memo(
  ({ catalogTypes, selectedTypeIndex, onSelectType, width }) => {
    const { isSmallVersion, isMediumVersion, scaleAll } = useAppContext();
    const styles = React.useMemo(() => createSlideStyles(scaleAll), [scaleAll]);

    const cardWidth = React.useMemo(() => {
      if (isSmallVersion) return "48.7%";
      if (isMediumVersion) return "49.1%";
      return width > 1440 ? "24.2%" : "32.66%";
    }, [isMediumVersion, isSmallVersion, width]);

    return (
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.shell}>
          <View style={styles.hero}>
            <Text selectable={false} style={styles.heroTitle}>
              Каталог Deante
            </Text>
            <Text selectable={false} style={styles.heroSubtitle}>
              Выберите категорию, чтобы сразу перейти к вариантам и фильтрам.
            </Text>
          </View>

          <Box>
            <View style={styles.grid}>
              {catalogTypes?.map((item, index) => (
                <TypeCard
                  key={`${item.type}_${index}`}
                  item={item}
                  isActive={selectedTypeIndex === index}
                  width={cardWidth as any}
                  scaleAll={scaleAll}
                  onPress={() => onSelectType(index)}
                />
              ))}
            </View>
          </Box>
        </View>
      </ScrollView>
    );
  }
);

export default TypeSlide;
